// Copyright 2023, Durable
import React, { Children, ReactElement, useEffect, useState } from "react";

/**
 * Checks if a given slot is populated by any elements.
 */
export function slotIsPopulated(name: string, elements: ReactElement) {
  let slotPopulated = false;
  Children.forEach(elements, (element) => {
    if (element?.props?.slot == name) {
      slotPopulated = true;
    }
  });
  return slotPopulated;
}

/**
 * A component to handle flexible slots inside a component. Follows an
 * identical API to other frameworks that support this functionality- if a slot
 * is named and a child has a matching slot prop, it'll render to that slot.
 * If a slot is unnamed, it will render all children without a slot prop.
 *
 * If nothing is rendered, it will fall back to the contents of the slot.
 */
function Slot({
  children,
  name,
  elements
}: {
  children?: ReactElement;
  name?: string;
  elements: ReactElement;
}): ReactElement {
  const [slotElements, setSlotElements] = useState<ReactElement[]>([]);

  useEffect(() => {
    if (name == null) {
      setSlotElements([]);
      Children.forEach(elements, (element) => {
        // If there is no name, render all unslotted children.
        if (element?.props?.slot == null) {
          setSlotElements((existingSlotElements) => [
            ...existingSlotElements,
            element
          ]);
        }
      });
    } else {
      setSlotElements([]);
      Children.forEach(elements, (element) => {
        // Render all children who belong in this slot
        if (element?.props?.slot == name) {
          setSlotElements((existingSlotElements) => [
            ...existingSlotElements,
            element
          ]);
        }
      });
    }
  }, [name, elements]);

  // If empty, return either the children as fallback or null. Otherwise, render
  // out all the elements that belong.
  return slotElements.length == 0 ? (
    children ?? null
  ) : (
    <>{slotElements.map((element) => element)}</>
  );
}

export default Slot;
